// #336: the round-structure actions of the phone editor (round menu, round
// ladder, «Per round» lens and its set count, S1). Pure `(draft, …) =>
// Draft`, re-exported by the barrel. Siblings of removeWeekAt
// (programMatrixEdits.ts) and share its contract: cells, notesPerCell,
// notesRawPerCell, inactiveWeeks and the draft's roundTypes are
// index-parallel, so a round moves all five or none.
import type { Draft, SlotDraft } from "./programEdit.js";
import { hasActiveWeek } from "./programBlocks.js";
import { MAX_CYCLE_WEEKS, setCycleWeeks, updateSlot } from "./programEditActions.js";
import { cloneWeekInDraft } from "./programEditWeekClone.js";
import { addSetAt, groupMemberIdxs, removeSetAt, setCountAt } from "./programEditRoundSets.js";
import { mapRoundTypes, roundTypeAt, setRoundType } from "./programRoundTypes.js";
import { MAX_ROUND_SETS } from "./schemas/program.js";

/** How many exercises run in round `week`: in day `sIdx`, or in every day. */
export const countActiveInWeek = (draft: Draft, week: number, sIdx?: number): number =>
  (sIdx === undefined ? draft.sessions : draft.sessions.slice(sIdx, sIdx + 1)).reduce(
    (n, s) => n + s.slots.filter((sl) => !sl.inactiveWeeks[week]).length,
    0,
  );

/** The columns in a new order: `order[w]` is the old index landing at `w`. */
const reorderWeeks = (draft: Draft, order: number[]): Draft => {
  const pick = <T,>(arr: T[]): T[] => order.map((i) => arr[i] as T);
  return {
    ...mapRoundTypes(draft, pick),
    sessions: draft.sessions.map((s) => ({
      ...s,
      slots: s.slots.map((sl) => ({
        ...sl,
        inactiveWeeks: pick(sl.inactiveWeeks),
        setRows: sl.setRows.map((r) => ({
          ...r,
          cells: pick(r.cells),
          notesPerCell: pick(r.notesPerCell),
          ...(r.notesRawPerCell ? { notesRawPerCell: pick(r.notesRawPerCell) } : {}),
        })),
      })),
    })),
  };
};

const inRange = (draft: Draft, ...weeks: number[]) =>
  weeks.every((w) => w >= 0 && w < draft.cycleWeeks);

/** Round `from` taken out and put back at `to`; the rounds between slide. */
const moveWeek = (draft: Draft, from: number, to: number): Draft => {
  if (from === to || !inRange(draft, from, to)) return draft;
  const order = Array.from({ length: draft.cycleWeeks }, (_, i) => i).filter((i) => i !== from);
  order.splice(to, 0, from);
  return reorderWeeks(draft, order);
};

/** Round `week` one place earlier or later. At an edge: the same draft. */
export const moveWeekAt = (draft: Draft, week: number, delta: -1 | 1): Draft =>
  moveWeek(draft, week, week + delta);

/** A copy of round `week` right after it (cloneWeekInDraft into a blank
 *  round grown at the end, then moved into place), its type included. At
 *  MAX_CYCLE_WEEKS: the same draft. A round nobody runs in copies as an
 *  empty round. */
export const duplicateWeekAt = (draft: Draft, week: number): Draft => {
  const n = draft.cycleWeeks;
  if (!inRange(draft, week) || n >= MAX_CYCLE_WEEKS) return draft;
  const grown = setCycleWeeks(draft, n + 1, "blank");
  if (grown.cycleWeeks !== n + 1) return draft;
  const cloned = cloneWeekInDraft(grown, week, [n]);
  const moved = moveWeek(cloned.ok ? cloned.draft : grown, n, week + 1);
  return setRoundType(moved, week + 1, roundTypeAt(draft, week));
};

/** Round `to` replaced by a copy of round `from`. `to` keeps its own type,
 *  as cloneWeekInDraft's targets do: a round that exists keeps its type.
 *  cloneWeekInDraft refuses a target anyone runs in, so the target is emptied
 *  first; an exercise that ran ONLY there is swept, as removeWeekAt sweeps. */
export const copyWeekOnto = (draft: Draft, from: number, to: number): Draft => {
  if (from === to || !inRange(draft, from, to)) return draft;
  const emptied: Draft = {
    ...draft,
    sessions: draft.sessions.map((s) => ({
      ...s,
      slots: s.slots.map((sl) => ({
        ...sl,
        inactiveWeeks: sl.inactiveWeeks.map((off, w) => (w === to ? true : off)),
      })),
    })),
  };
  const cloned = cloneWeekInDraft(emptied, from, [to]);
  const next = cloned.ok ? cloned.draft : emptied;
  return {
    ...next,
    sessions: next.sessions.map((s) => ({ ...s, slots: s.slots.filter(hasActiveWeek) })),
  };
};

export type SlotRoundResult =
  | { ok: true; draft: Draft }
  /** "lastRound": the exercise would run in no round (unsaveable).
   *  "lastInDay": the day would be empty in that round (emptyCell). */
  | { ok: false; reason: "lastRound" | "lastInDay" };

/** The nearest round the slot runs in: the earlier one first. */
const nearestActiveWeek = (slot: SlotDraft, week: number): number | null => {
  for (let w = week - 1; w >= 0; w--) if (!slot.inactiveWeeks[w]) return w;
  for (let w = week + 1; w < slot.inactiveWeeks.length; w++) {
    if (!slot.inactiveWeeks[w]) return w;
  }
  return null;
};

/** One exercise put in round `week`, or taken out. Slot-level, unlike the
 *  block-shaped togglePlacement. Added: its sets copy the nearest round it
 *  runs in; notes stay as stored. Taken out: the cells stay (the save emits
 *  no sets for an inactive round). Already so: ok, the same draft. */
export const setSlotRoundActive = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  week: number,
  active: boolean,
): SlotRoundResult => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  if (!slot || !inRange(draft, week)) return { ok: true, draft };
  if (!slot.inactiveWeeks[week] === active) return { ok: true, draft };
  const inactiveWeeks = slot.inactiveWeeks.map((off, w) => (w === week ? !active : off));
  if (!active) {
    if (inactiveWeeks.every((off) => off)) return { ok: false, reason: "lastRound" };
    if (countActiveInWeek(draft, week, sIdx) <= 1) return { ok: false, reason: "lastInDay" };
    return { ok: true, draft: updateSlot(draft, sIdx, slotIdx, { inactiveWeeks }) };
  }
  const from = nearestActiveWeek(slot, week);
  const setRows =
    from === null
      ? slot.setRows
      : slot.setRows.map((r) => ({
          ...r,
          cells: r.cells.map((c, w) => (w === week && r.cells[from] ? { ...r.cells[from]! } : c)),
        }));
  return { ok: true, draft: updateSlot(draft, sIdx, slotIdx, { inactiveWeeks, setRows }) };
};

/** The slots a per-round set change applies to: the slot, or its superset
 *  (Q7: the group stays symmetric round by round). */
const roundMembers = (draft: Draft, sIdx: number, slotIdx: number): number[] => {
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const gid = slots[slotIdx]?.group?.id;
  return gid ? groupMemberIdxs(slots, gid) : [slotIdx];
};

export type RoundSetResult =
  | { ok: true; draft: Draft }
  /** "lastSet": the round's only set. "mixedBound": a set would move into a
   *  row bound by reps in one round and by time in another. */
  | { ok: false; reason: "lastSet" | "mixedBound" };

/** #336 S1, the «Per round» lens: one more set in round `week` only, a copy
 *  of its last one, on every member of the superset that runs there. A
 *  member that refuses leaves the draft as it was. */
export const addSetInRound = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  week: number,
): RoundSetResult => {
  if (!inRange(draft, week)) return { ok: true, draft };
  let next = draft;
  for (const i of roundMembers(draft, sIdx, slotIdx)) {
    const slot = next.sessions[sIdx]?.slots[i];
    if (!slot || slot.inactiveWeeks[week]) continue;
    const grown = addSetAt(slot, week);
    if (grown === slot) return { ok: false, reason: "mixedBound" };
    next = updateSlot(next, sIdx, i, grown);
  }
  return { ok: true, draft: next };
};

/** #336 S1: set `rowIdx` out of round `week` only, on every member of the
 *  superset that runs there. A member that refuses leaves the draft as it
 *  was; the round's only set stays (the round is taken out with
 *  setSlotRoundActive instead). */
export const removeSetInRound = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  week: number,
  rowIdx: number,
): RoundSetResult => {
  if (!inRange(draft, week)) return { ok: true, draft };
  let next = draft;
  for (const i of roundMembers(draft, sIdx, slotIdx)) {
    const slot = next.sessions[sIdx]?.slots[i];
    if (!slot || slot.inactiveWeeks[week]) continue;
    const cut = removeSetAt(slot, week, rowIdx);
    if (!cut) return { ok: false, reason: setCountAt(slot, week) <= 1 ? "lastSet" : "mixedBound" };
    next = updateSlot(next, sIdx, i, cut);
  }
  return { ok: true, draft: next };
};

/** setSetCountInRound's answer: `count`, the sets the round has after it. */
export type RoundSetCountResult =
  | { ok: true; draft: Draft; count: number }
  | { ok: false; reason: "lastSet" | "mixedBound" };

/** #336 brief §15 row 36, the wide grid's «Sets» lens: round `week` gets `n`
 *  sets, in that round only, on every member of the superset. Built on
 *  addSetInRound and removeSetInRound, one set at a time from the last, so
 *  their refusals hold. Above MAX_ROUND_SETS it stops there; below 1 is
 *  refused as the last set is. */
export const setSetCountInRound = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  week: number,
  n: number,
): RoundSetCountResult => {
  if (n < 1) return { ok: false, reason: "lastSet" };
  const want = Math.min(Math.floor(n), MAX_ROUND_SETS);
  const start = draft.sessions[sIdx]?.slots[slotIdx];
  if (!start || start.inactiveWeeks[week]) return { ok: true, draft, count: 0 };
  let next = draft;
  let have = setCountAt(start, week);
  // One step per set to add or take out: bounded, whatever a step does.
  for (let i = Math.abs(have - want); i > 0 && have !== want; i -= 1) {
    const res =
      have < want ? addSetInRound(next, sIdx, slotIdx, week) : removeSetInRound(next, sIdx, slotIdx, week, have - 1);
    if (!res.ok) return res;
    next = res.draft;
    have = setCountAt(next.sessions[sIdx]!.slots[slotIdx]!, week);
  }
  return { ok: true, draft: next, count: have };
};
